<template>
  <div class="loan-details-step">

    <!-- ── Amount slider (desktop only) ── -->
    <FormGroup class="slider-wrap">
      <VueSlider
        v-model="form.sliderAmount"
        :marks="mark => mark % 250 === 0"
        :max="__amountRange.MAX"
        :min="__amountRange.MIN"
        :interval="50"
        :tooltip-formatter="val => `$${Number(val).toLocaleString()}`"
        :disabled="isTopUp"
        tooltip="always"
        tooltip-placement="top"
      />
    </FormGroup>

    <!-- ── Loan summary pills ── -->
    <div class="loan-stat-bar">
      <div class="loan-stat">
        <LoanTypeDisplayCard :amount="form.amount" class="loan-stat__card"/>
      </div>
      <div class="loan-stat-divider"/>
      <div class="loan-stat">
        <RepaymentAmountDisplayCard
          :amount="form.amount"
          :duration="form.terms"
          :frequency="form.frequency"
          class="loan-stat__card"
        />
      </div>
    </div>

    <!-- ── Loan selectors ── -->
    <div class="row mt-4">
      <div class="col-md-4">
        <FormGroup :error-container="errorContainerName" label="Borrow Amount" name="amount" :floating="true">
          <FmSelect
            :value="form.amount"
            :options="amountOptions"
            :icons="amountIcons"
            :disabled="isTopUp"
            @input="form.amount = Number($event)"
          />
        </FormGroup>
      </div>
      <div class="col-md-4">
        <FormGroup :error-container="errorContainerName" label="Duration" name="terms" :floating="true">
          <FmSelect
            :value="form.terms"
            :options="durationOptions"
            :icons="durationIcons"
            :disabled="isTopUp"
            @input="form.terms = Number($event)"
          />
        </FormGroup>
      </div>
      <div class="col-md-4">
        <FormGroup :error-container="errorContainerName" label="Repayment Frequency" name="frequency" :floating="true">
          <FmSelect
            :value="form.frequency"
            :options="frequencies"
            :icons="freqIcons"
            :disabled="isTopUp"
            @input="form.frequency = $event"
          />
        </FormGroup>
      </div>
    </div>

    <Checkbox
      v-if="!isTopUp"
      v-model="form.agreeLoanAmountCanChange"
      :error-container="errorContainerName"
      name="agreeLoanAmountCanChange"
    >
      <template v-slot="{ onClick, style }">
        <div @click="onClick">
          I understand that subject to Flash Money's lending criteria, the final loan amount and term may differ from
          my application.
        </div>
      </template>
    </Checkbox>

    <input name="paying_loan_amount" type="hidden" value="0">
    <input id="ga_user_id" :value="ga_user_id" name="ga_user_id" type="hidden">

    <p id="loan-purpose-label" class="fm-section-label">Loan Purpose</p>
    <FormGroup :error-container="errorContainerName" name="purpose.description">
      <LoanPurposePicker v-model="form.purpose.description" aria-labelledby="loan-purpose-label"/>
    </FormGroup>

    <FormGroup
      v-if="!!selectedPurpose"
      :error-container="errorContainerName"
      label="Additional purpose details"
      name="purpose.reason"
      :floating="true"
    >
      <textarea v-model="form.purpose.reason" class="form-control" name="loan_purpose_details" rows="3" placeholder=" "/>
    </FormGroup>

    <FormGroup
      :error-container="errorContainerName"
      label="Can you foresee any reason you won't be able to repay this loan?"
      name="inabilityToRepay"
    >
      <RadioButtonGroup
        v-model="form.inabilityToRepay"
        :block="true"
        :options="{no: 'No', yes: 'Yes'}"
        name="inability_to_repay"
        size="sm"
      />
    </FormGroup>

    <FormGroup
      v-if="form.inabilityToRepay === 'yes'"
      :error-container="errorContainerName"
      label="Please describe the reason"
      name="inabilityToRepayReason"
      :floating="true"
    >
      <textarea
        id="reason_for_foresee"
        v-model="form.inabilityToRepayReason"
        class="form-control"
        name="reason_for_foresee"
        placeholder=" "
      />
    </FormGroup>

    <FormGroup
      :error-container="errorContainerName"
      label="Acknowledgement & Consent"
      name="acknowledgementAndConsentGranted"
    >
      <AcknowledgementAndConsent v-model="form.acknowledgementAndConsentGranted"/>
    </FormGroup>

    <slot name="controls" v-bind="{ prev: null, next: onNext }"/>
  </div>
</template>

<script>
import NewApplicationStepMixin from '@modules/guest/new-applicant/components/mixins/NewApplicationStepMixin';
import {
  AmountTerms,
  ApplicationChannel,
  Frequencies,
  LoanAmountRange,
  LoanPurposes,
  LoanTypes,
} from '@modules/guest/new-applicant/constants';
import RadioButtonGroup from '@modules/shared/components/form/RadioButtonGroup';
import AcknowledgementAndConsent from '../../modals/AcknowledgementAndConsent';
import FormGroup from '@modules/shared/components/form/FormGroup';
import VueSlider from 'vue-slider-component';
import {NEW_APPLICATION} from '@store/constants';
import {validate, validateAmount} from './formValidator';
import '../../scss/vue-slider.scss';
import {scrollToErrorFormField} from '@modules/shared/helpers/form';
import Checkbox from '@modules/shared/components/form/FmCheckbox.vue';
import store from '@modules/guest/new-applicant/store/store';
import {saveAsDraft} from '@modules/guest/new-applicant/NewApplicationService';
import {mergeDeep} from '@modules/shared/helpers/object-fns';
import RepaymentAmountDisplayCard from './components/RepaymentAmountDisplayCard';
import LoanPurposePicker from '@modules/guest/new-applicant/components/steps/LoanDetails/components/LoanPurposePicker';
import FmSelect from '@modules/shared/components/form/FmSelect';

const Purposes = LoanPurposes;

// ── Icons for the loan-selector FmSelects ──────────────────────────────────
const DOLLAR_ICON = `<svg xmlns="http://www.w3.org/2000/svg" fill="currentColor" viewBox="0 0 16 16"><path d="M4 10.781c.148 1.667 1.513 2.85 3.591 3.003V15h1.043v-1.216c2.27-.179 3.678-1.438 3.678-3.3 0-1.59-.947-2.51-2.956-3.028l-.722-.187V3.467c1.122.11 1.879.714 2.07 1.616h1.47c-.166-1.6-1.54-2.748-3.54-2.875V1H7.591v1.233c-1.939.23-3.27 1.472-3.27 3.156 0 1.454.966 2.483 2.661 2.917l.61.162v4.031c-1.149-.17-1.94-.8-2.131-1.718H4zm3.391-3.836c-1.043-.263-1.6-.825-1.6-1.616 0-.944.704-1.641 1.8-1.828v3.495l-.2-.051zm1.591 1.872c1.287.323 1.852.859 1.852 1.769 0 1.097-.826 1.828-2.2 1.939V8.73l.348.086z"/></svg>`;
const CALENDAR_ICON = `<svg xmlns="http://www.w3.org/2000/svg" fill="currentColor" viewBox="0 0 16 16"><path d="M3.5 0a.5.5 0 0 1 .5.5V1h8V.5a.5.5 0 0 1 1 0V1h1a2 2 0 0 1 2 2v11a2 2 0 0 1-2 2H2a2 2 0 0 1-2-2V3a2 2 0 0 1 2-2h1V.5a.5.5 0 0 1 .5-.5zM1 4v10a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1V4H1z"/></svg>`;
const FREQ_ICONS = {
  weekly:      `<svg xmlns="http://www.w3.org/2000/svg" fill="currentColor" viewBox="0 0 16 16"><path d="M11 6.5a.5.5 0 0 1 .5-.5h1a.5.5 0 0 1 .5.5v1a.5.5 0 0 1-.5.5h-1a.5.5 0 0 1-.5-.5v-1z"/><path d="M3.5 0a.5.5 0 0 1 .5.5V1h8V.5a.5.5 0 0 1 1 0V1h1a2 2 0 0 1 2 2v11a2 2 0 0 1-2 2H2a2 2 0 0 1-2-2V3a2 2 0 0 1 2-2h1V.5a.5.5 0 0 1 .5-.5zM1 4v10a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1V4H1z"/></svg>`,
  fortnightly: `<svg xmlns="http://www.w3.org/2000/svg" fill="currentColor" viewBox="0 0 16 16"><path d="M6.5 6a.5.5 0 0 0-.5.5v3a.5.5 0 0 0 .5.5h3a.5.5 0 0 0 .5-.5v-3a.5.5 0 0 0-.5-.5h-3z"/><path d="M3.5 0a.5.5 0 0 1 .5.5V1h8V.5a.5.5 0 0 1 1 0V1h1a2 2 0 0 1 2 2v11a2 2 0 0 1-2 2H2a2 2 0 0 1-2-2V3a2 2 0 0 1 2-2h1V.5a.5.5 0 0 1 .5-.5zM1 4v10a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1V4H1z"/></svg>`,
  monthly:     `<svg xmlns="http://www.w3.org/2000/svg" fill="currentColor" viewBox="0 0 16 16"><path d="M3.5 0a.5.5 0 0 1 .5.5V1h8V.5a.5.5 0 0 1 1 0V1h1a2 2 0 0 1 2 2v11a2 2 0 0 1-2 2H2a2 2 0 0 1-2-2V3a2 2 0 0 1 2-2h1V.5a.5.5 0 0 1 .5-.5zM1 4v10a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1V4H1zm5 3a1 1 0 0 1 1-1h2a1 1 0 0 1 1 1v2a1 1 0 0 1-1 1H7a1 1 0 0 1-1-1V7z"/></svg>`,
};
const ERROR_CONTAINER_NAME = 'NewApplication::LoanDetailsStepForm';
const AMOUNT_INCREMENT_BY = 50;

const AMOUNT_IN_50_INCREMENT = (({ MAX, MIN }, incrementBy) => {
  const validAmounts = [];
  const maxCount = (MAX - MIN) / incrementBy;
  for (let i = 0; i <= maxCount; i++) {
    validAmounts.push((i * incrementBy) + MIN);
  }
  return validAmounts;
})(LoanAmountRange, AMOUNT_INCREMENT_BY);

const toNumber = amount => Number(amount);

export const getLoanTypeByAmount = (amount) => toNumber(amount) > 2000 ? 'MACC' : 'SACC';

export const generateTerms = (amount) => {
  for (const key in AmountTerms) {
    const terms = Number(key);
    const match = AmountTerms[terms];
    if (match(amount)) return Array.from({length: terms}, (_, i) => i + 1);
  }
  return [];
};

const LOAN_TYPE_DESCRIPTIONS = {
  SACC: 'Loans up to $2,000',
  MACC: 'Loans from $2,001',
};

const LoanTypeDisplayCard = {
  name: 'LoanTypeSelector',
  template: `
    <div class="loan-stat__inner">
      <div class="loan-stat__value" v-text="types[type]"/>
      <div class="loan-stat__desc" v-text="descriptions[type]"/>
      <div class="loan-stat__label">Loan Type</div>
    </div>
  `,
  components: {FormGroup},
  props: ['amount'],
  computed: {
    type() { return getLoanTypeByAmount(this.amount) || 'SACC'; },
    types: () => LoanTypes,
    descriptions: () => LOAN_TYPE_DESCRIPTIONS,
  },
};

const defaultState = () => ({
  amount: LoanAmountRange.MIN,
  frequency: 'weekly',
  sliderAmount: LoanAmountRange.MIN,
  agreeLoanAmountCanChange: null,
  terms: 1,
  purpose: {
    description: Purposes[Purposes.length - 1].description,
    reason: '',
  },
  inabilityToRepay: null,
  inabilityToRepayReason: '',
  acknowledgementAndConsentGranted: false,
});

export default {
  name: 'LoanDetails',
  mixins: [NewApplicationStepMixin],
  components: {
    LoanPurposePicker,
    Checkbox,
    RadioButtonGroup,
    AcknowledgementAndConsent,
    FormGroup,
    FmSelect,
    LoanTypeDisplayCard,
    RepaymentAmountDisplayCard,
    VueSlider,
  },
  data() {
    const cache = store.state[NEW_APPLICATION.MODULE_NAME].cache;
    const isTopUp = cache?.channel === ApplicationChannel.CUSTOMER_PORTAL_TOP_UP;
    const isReApply = cache?.channel === ApplicationChannel.CUSTOMER_PORTAL_RE_APPLY;
    // Preserve any stored checkbox answers so they survive a page refresh.
    // Only fall back to the default blank/false values when there is genuinely no stored state
    // (i.e. the user has never visited this step before).
    const stored = store.state[NEW_APPLICATION.MODULE_NAME].form.LoanDetails || {};
    const overrides = (isTopUp || isReApply)
      ? {}
      : {
          agreeLoanAmountCanChange: stored.agreeLoanAmountCanChange ?? null,
          inabilityToRepay: stored.inabilityToRepay ?? null,
          acknowledgementAndConsentGranted: stored.acknowledgementAndConsentGranted ?? false,
        };
    const form = mergeDeep(
      defaultState(),
      store.state[NEW_APPLICATION.MODULE_NAME].form.LoanDetails,
      overrides,
    );
    form.sliderAmount = form.amount;
    return {form};
  },
  computed: {
    __amountRange: () => LoanAmountRange,
    __amounts: () => [...AMOUNT_IN_50_INCREMENT],
    selectedPurpose() {
      return Purposes.filter(p => p.description === this.form.purpose.description)[0] || null;
    },
    loanDurations() {
      return generateTerms(toNumber(this.form.sliderAmount) || 500);
    },
    frequencies: () => Frequencies,
    // ── FmSelect option/icon maps ──────────────────────────────────────────
    amountOptions() {
      return Object.fromEntries(this.__amounts.map(m => [m, `$${m.toFixed(0)}`]));
    },
    amountIcons() {
      return Object.fromEntries(this.__amounts.map(m => [m, DOLLAR_ICON]));
    },
    durationOptions() {
      return Object.fromEntries(
        this.loanDurations.map(n => [n, `${n} Month${n > 1 ? 's' : ''}`])
      );
    },
    durationIcons() {
      return Object.fromEntries(this.loanDurations.map(n => [n, CALENDAR_ICON]));
    },
    freqIcons: () => FREQ_ICONS,
    ga_user_id() { return 1; },
    code() { return store.state[NEW_APPLICATION.MODULE_NAME].code; },
    isTopUp() {
      const cache = store.state[NEW_APPLICATION.MODULE_NAME].cache;
      return cache?.channel === ApplicationChannel.CUSTOMER_PORTAL_TOP_UP;
    },
  },
  watch: {
    form: {
      deep: true,
      immediate: true,
      handler() {
        if (this.shouldGoNextStep()) this.syncWithStore();
      },
    },
    ['form.sliderAmount'](amount) {
      if (validateAmount({amount}) === null) this.form.amount = amount;
    },
    ['form.amount'](amount) {
      if (validateAmount({amount}) === null) this.form.sliderAmount = amount;
    },
    loanDurations(durations) {
      if (!durations.includes(this.form.terms)) this.form.terms = 1;
    },
    isTopUp: {
      immediate: true,
      handler(value) {
        if (value) this.form.agreeLoanAmountCanChange = true;
      },
    },
  },
  methods: {
    onNext() {
      if (!this.shouldGoNextStep()) {
        scrollToErrorFormField(this.$el);
        return;
      }
      this.saveAndSync().then(async () => {
        await store.dispatch(NEW_APPLICATION.ACTIONS.NEXT_STEP);
      }).catch(e => {
        this.handleServerError(e);
        scrollToErrorFormField(this.$el);
      });
    },
    async saveAndSync() {
      const payload = { step: 'LoanDetails', ...this.form };
      store.commit({type: NEW_APPLICATION.MUTATIONS.SET_STEPS_META, payload: {loading: true}});
      try {
        const {data: {draft, otp, loanNumber, canPerformCS}} = await saveAsDraft(this.code, payload);
        store.commit({type: NEW_APPLICATION.MUTATIONS.SET_META, payload: {otp, loanNumber, canPerformCS}});
        store.commit({type: NEW_APPLICATION.MUTATIONS.SET_CACHE, payload: draft});
      } catch (e) { throw e; }
      finally { store.commit({type: NEW_APPLICATION.MUTATIONS.SET_STEPS_META, payload: {loading: false}}); }
    },
    syncWithStore() {
      this.$store.commit({ type: NEW_APPLICATION.MUTATIONS.SET_LOAN_DETAILS, payload: { ...this.form } });
    },
    validate,
  },
  created() {
    this.registerErrorContainer(ERROR_CONTAINER_NAME);
  },
};
</script>

<style lang="scss">
$fm-ink:       #15171C;
$fm-ink-soft:  #5B606B;
/* WCAG 1.4.3: was #9197A1 (~2.94:1 on white/#FBFAF6, fails 4.5:1). #6B7280
   clears AA for .loan-stat__label / .fm-section-label text below. */
$fm-ink-faint: #6B7280;
$fm-line:      #E7E4DA;
$fm-line-soft: #F0EEE7;
$fm-surface:   #FBFAF6;
$fm-amber:     #FFB000;

.loan-details-step {

  // Slider strip
  .slider-wrap {
    padding: 2rem 3rem 1.5rem;
    background: $fm-surface;
    margin: -2rem -2rem 1.5rem;
    border-bottom: 1px solid $fm-line-soft;

    @media (max-width: 995.999px) { display: none !important; }
  }

  // ── Loan stat bar ──────────────────────────────────────────────
  .loan-stat-bar {
    display: flex;
    align-items: stretch;
    gap: 0;
    background: #fff;
    border: 1px solid $fm-line;
    border-radius: 14px;
    overflow: hidden;
    margin-bottom: 1.75rem;
  }

  .loan-stat {
    flex: 1;
    padding: 1.1rem 1.4rem;

    &-divider {
      width: 1px;
      background: $fm-line;
    }
  }

  .loan-stat__inner {
    display: flex;
    flex-direction: column;
    gap: 4px;
  }

  .loan-stat__value {
    font-size: 1.55rem;
    font-weight: 800;
    letter-spacing: -0.5px;
    color: $fm-ink;
    line-height: 1.1;
    font-family: inherit;
  }

  .loan-stat__desc {
    font-size: 12.5px;
    font-weight: 500;
    color: $fm-ink-soft;
    line-height: 1.3;
    margin-top: 1px;
  }

  .loan-stat__label {
    font-size: 11px;
    font-weight: 600;
    color: $fm-ink-faint;
    text-transform: uppercase;
    letter-spacing: 0.06em;
    margin-top: 4px;
  }

  @media (max-width: 576px) {
    .loan-stat { padding: 1rem 1rem; }
    .loan-stat__value { font-size: 1.2rem; }
  }

  .fm-section-label {
    font-size: 11px;
    font-weight: 700;
    letter-spacing: 0.07em;
    text-transform: uppercase;
    color: $fm-ink-faint;
    margin: 0.5rem 0 0.5rem;
  }
}
</style>
